import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import CompanyLogo from '../components/CompanyLogo';
import { useMember } from '../contexts/MemberContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Transaction Confirmation Page
 * Shows confirmation message and flashing banner after a transaction
 */
const TransactionConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { primaryColor, secondaryColor } = useAuth();
  const { orientation, setOrientation } = useMember();
  const [bannerColor, setBannerColor] = useState(primaryColor || '#1976d2');
  const amount = location.state?.amount || '0.00';

  // Flashing banner effect
  useEffect(() => {
    const interval = setInterval(() => {
      setBannerColor(prevColor =>
        prevColor === (primaryColor || '#1976d2')
          ? (secondaryColor || '#dc004e')
          : (primaryColor || '#1976d2')
      );
    }, 800); // Toggle color every 800ms

    return () => clearInterval(interval);
  }, [primaryColor, secondaryColor]);

  // Function to handle going back to landing page
  const handleHomeClick = () => {
    navigate('/landing');
  };
  useEffect(() => {
    if (orientation === 1) {
      // the device was flipped over so navigate to the points earned page
      navigate('/points-earned', {
        state: {
          totalPoints: location.state?.totalPoints || 0,
          pointsEarned: location.state?.pointsEarned || 0
        }
      });
    }
  }, [orientation, location.state]);

  // Handle user icon click to switch to customer side
  const handlePersonClick = () => {
    setOrientation(1); // Flip orientation to show member dashboard
  };

  return (
    <Container sx={{
      maxWidth: '100vw !important',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: '0 !important',
      py: 4
    }}>
      {/* Navigation buttons */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, maxWidth: '1200px', width: '100%', padding: '0 24px' }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleHomeClick}
          startIcon={<LogoutIcon />}
          size="medium"
        >
          Log Out
        </Button>

        <CompanyLogo />

        <IconButton
          onClick={handlePersonClick}
          color="text.secondary"
          aria-label="customer view"
          size="large"
        >
          <PersonIcon fontSize="large" />
        </IconButton>
      </Stack>

      {/* Main content area */}
      <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        {/* Flashing banner */}
        <Paper
          elevation={3}
          sx={{
            width: '100vw',
            height: '30vh',
            p: 4,
            my: 4,
            backgroundColor: bannerColor,
            color: 'white',
            transition: 'background-color 0.4s ease',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h3"
            align="center"
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            Transaction Recorded
          </Typography>
          <Typography
            variant="h4"
            align="center"
          >
            When ready, please flip the tablet back toward the customer
          </Typography>
        </Paper>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body3" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default TransactionConfirmationPage;